<template>
  <div class="ua-page">

    <!-- Hero -->
    <div class="ua-hero">
      <div class="d-flex align-center" style="gap:14px;">
        <v-avatar color="rgba(255,255,255,0.15)" size="46">
          <v-icon color="white" size="26">mdi-account-plus-outline</v-icon>
        </v-avatar>
        <div>
          <div class="ua-hero__title">Registrar Nuevo Usuario</div>
          <div class="ua-hero__sub">Datos personales y credenciales de acceso al sistema</div>
        </div>
      </div>
      <v-btn small dark text @click="$router.back()">
        <v-icon left small>mdi-arrow-left</v-icon> Volver
      </v-btn>
    </div>

    <form @submit.prevent="validarCampos" novalidate>
      <div class="ua-body">

        <!-- Paso 1: Buscar en talento humano -->
        <v-card outlined class="ua-section-card mb-4">
          <v-card-title class="ua-section-title py-2 px-4">
            <v-icon small color="purple darken-2" class="mr-1">mdi-account-search-outline</v-icon>
            Paso 1 — Verificar en Talento Humano
          </v-card-title>
          <v-card-text class="pt-3 pb-3 px-4">
            <div class="purple--text text--darken-1 mb-3" style="font-size:0.83rem;">
              Primero busque por carnet si existe en el sistema de talento humano:
            </div>
            <v-row dense>
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="persona.num_doc"
                  label="Documento de identidad"
                  :loading="buscando"
                  :disabled="buscando"
                  clearable outlined dense
                  prepend-inner-icon="mdi-card-account-details-outline"
                  append-icon="mdi-file-find"
                  @click:append="buscarTalento"
                  :error-messages="validation.firstError('persona.num_doc')"
                />
              </v-col>
              <v-col cols="12" sm="3">
                <v-btn block color="purple darken-1" dark depressed :loading="buscando" @click="buscarTalento">
                  <v-icon left small>mdi-magnify</v-icon> Verificar
                </v-btn>
              </v-col>
              <v-col cols="12" sm="4" class="d-flex align-center" v-if="existe_in_talento">
                <v-chip small color="green lighten-5" class="green--text text--darken-3">
                  <v-icon left small>mdi-check-circle-outline</v-icon> Encontrado en Talento Humano
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <template v-if="existe_in_talento">

          <!-- Datos personales -->
          <v-card outlined class="ua-section-card mb-4">
            <v-card-title class="ua-section-title py-2 px-4">
              <v-icon small color="indigo darken-2" class="mr-1">mdi-account-outline</v-icon>
              Datos Personales
            </v-card-title>
            <v-card-text class="pt-4 pb-2 px-4">
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.trim="persona.nombres"
                    label="Nombres *"
                    outlined dense
                    prepend-inner-icon="mdi-account-outline"
                    :error-messages="validation.firstError('persona.nombres')"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.trim="persona.pri_apellido"
                    label="Apellido 1 *"
                    outlined dense
                    prepend-inner-icon="mdi-account-outline"
                    :error-messages="validation.firstError('persona.pri_apellido')"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.trim="persona.seg_apellido"
                    label="Apellido 2"
                    outlined dense
                    prepend-inner-icon="mdi-account-outline"
                    :error-messages="validation.firstError('persona.seg_apellido')"
                  />
                </v-col>

                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.trim="persona.email"
                    label="Correo electrónico *"
                    outlined dense
                    prepend-inner-icon="mdi-email-outline"
                    :error-messages="validation.firstError('persona.email')"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model.trim="persona.sexo"
                    label="Sexo *"
                    :items="[{v:'F',t:'Mujer'},{v:'M',t:'Hombre'},{v:'O',t:'Otros'}]"
                    item-value="v" item-text="t"
                    outlined dense
                    prepend-inner-icon="mdi-gender-male-female"
                    :error-messages="validation.firstError('persona.sexo')"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model.trim="persona.diestro"
                    label="Diestro *"
                    :items="[{v:'true',t:'Sí'},{v:'false',t:'No'}]"
                    item-value="v" item-text="t"
                    outlined dense
                    prepend-inner-icon="mdi-hand-back-right-outline"
                    :error-messages="validation.firstError('persona.diestro')"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <div class="ua-field-label mb-1">Expedido (Departamento)</div>
                  <Departamentos :model="persona" ref="refDepartamentos" />
                  <div class="ua-field-error" v-if="validation.firstError('persona.iddepartamento')">
                    {{ validation.firstError('persona.iddepartamento') }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="ua-field-label mb-1">Profesiones *</div>
                  <Profesiones :model="persona" v-bind:multiple="true" ref="refProfesiones" />
                  <div class="ua-field-error" v-if="validation.firstError('persona.profesiones')">
                    {{ validation.firstError('persona.profesiones') }}
                  </div>
                </v-col>

                <v-col cols="12" sm="4">
                  <div class="ua-field-label mb-1">País</div>
                  <Paises :model="persona" />
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="ua-field-label mb-1">Institución de egreso</div>
                  <Universidades :model="persona" />
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="ua-field-label mb-1">Ciudad</div>
                  <Ciudades :model="persona" />
                </v-col>

                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.trim="persona.telefono1"
                    label="Teléfono 1"
                    outlined dense
                    prepend-inner-icon="mdi-phone-outline"
                    :error-messages="validation.firstError('persona.telefono1')"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.trim="persona.telefono2"
                    label="Teléfono 2"
                    outlined dense
                    prepend-inner-icon="mdi-phone-outline"
                    :error-messages="validation.firstError('persona.telefono2')"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.trim="persona.domicilio"
                    label="Domicilio"
                    outlined dense
                    prepend-inner-icon="mdi-home-outline"
                    :error-messages="validation.firstError('persona.domicilio')"
                  />
                </v-col>

                <v-col cols="12" sm="4">
                  <div class="ua-field-label mb-1">Fecha de nacimiento</div>
                  <Fecha :model="persona" valueto="fecha_nac" ref="refFecha" />
                  <div class="ua-field-error" v-if="validation.firstError('persona.fecha_nac')">
                    {{ validation.firstError('persona.fecha_nac') }}
                  </div>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.trim="persona.usuario_moodle"
                    label="Usuario Moodle"
                    outlined dense
                    prepend-inner-icon="mdi-school-outline"
                    :error-messages="validation.firstError('persona.usuario_moodle')"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.trim="persona.clave_moodle"
                    label="Clave Moodle"
                    outlined dense
                    prepend-inner-icon="mdi-lock-outline"
                    :error-messages="validation.firstError('persona.clave_moodle')"
                  />
                </v-col>

                <v-col cols="12">
                  <Menus />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Credenciales de acceso -->
          <v-card outlined class="ua-section-card mb-4">
            <v-card-title class="ua-section-title py-2 px-4">
              <v-icon small color="indigo darken-2" class="mr-1">mdi-shield-key-outline</v-icon>
              Credenciales de Acceso al Sistema
            </v-card-title>
            <v-card-text class="pt-4 pb-2 px-4">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.trim="usuario.login"
                    label="Usuario *"
                    outlined dense
                    prepend-inner-icon="mdi-account-circle-outline"
                    placeholder="Login de acceso al sistema"
                    :error-messages="validation.firstError('usuario.login')"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.trim="usuario.clave"
                    label="Clave *"
                    outlined dense
                    prepend-inner-icon="mdi-lock-outline"
                    placeholder="Clave de acceso al sistema"
                    :error-messages="validation.firstError('usuario.clave')"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.trim="usuario.correo_corp"
                    label="Correo corporativo"
                    outlined dense
                    prepend-inner-icon="mdi-email-lock-outline"
                    :error-messages="validation.firstError('usuario.correo_corp')"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.trim="usuario.observacion"
                    label="Observación"
                    outlined dense
                    prepend-inner-icon="mdi-note-text-outline"
                    :error-messages="validation.firstError('usuario.observacion')"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Cargos y sedes -->
          <v-card outlined class="ua-section-card mb-4">
            <v-card-title class="ua-section-title py-2 px-4">
              <v-icon small color="indigo darken-2" class="mr-1">mdi-briefcase-account-outline</v-icon>
              Cargos y Sedes
            </v-card-title>
            <v-card-text class="pt-3 pb-2 px-4">
              <div v-if="validation.firstError('cargos')" class="red--text text--darken-1 mb-2" style="font-size:0.83rem;">
                <v-icon small color="red darken-1">mdi-alert-circle-outline</v-icon>
                Indique los cargos y sedes
              </div>
              <Asignar :model="cargos" />
            </v-card-text>
          </v-card>

          <!-- Alertas + submit -->
          <v-card outlined class="ua-section-card mb-4">
            <v-card-text class="px-4 py-3">
              <Alertas ref="alertar" />
              <p v-if="msg" class="text-center mt-2 grey--text">{{ msg }}</p>
              <div class="d-flex justify-center mt-3">
                <v-btn color="indigo darken-1" dark depressed :disabled="!activo" type="submit" large>
                  <v-icon left>mdi-content-save-outline</v-icon> Registrar Usuario
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

        </template>

      </div>
    </form>

    <notifications group="auth" position="bottom right" />
  </div>
</template>

<script>
import { Validator } from 'simple-vue-validator';
import gql from 'graphql-tag'
import Profesiones from '@/components/AProfesiones.vue'
import Departamentos from '@/components/ADepartamentos.vue'
import Paises from '@/components/APaises.vue'
import Universidades from '@/components/AUniversidades.vue'
import Ciudades from '@/components/ACiudades.vue'
import Fecha from '@/components/AFecha.vue'
import Asignar from '@/components/AAsignarCargoAdd.vue'
import Alertas from '@/components/AAlertas.vue'
import Menus from '@/components/AMenus.vue'
import { parseJsonError } from '@/components/Utils.js'

export default {
  components: { Profesiones, Departamentos, Paises, Universidades, Ciudades, Fecha, Asignar, Alertas, Menus },
  data() {
    return {
      activo: true,
      msg: '',
      universidades: [],
      postgrados: [],
      sedes: [],
      gestiones: [],
      persona: {
        num_doc: null,
        nombres: null,
        pri_apellido: null,
        email: null,
        sexo: null,
        tipo: 'P',
        diestro: true,
        idprofesion: null,
        profesiones: [],
        grados: [],
        idpais: null,
        iddepartamento: null,
        idinstitucion: null,
        idciudad: null,
        contacto: null,
        datos_laborales: null,
        clave_moodle: null,
        usuario_moodle: null,
        est_civil: null,
        fecha_nac: null,
        telefono2: null,
        telefono1: null,
        domicilio: null,
        seg_apellido: null,
      },
      usuario: {
        login: null,
        clave: null,
        correo_corp: null,
        observacion: null,
        foto: 'userdefault',
      },
      cargos: [],
      existe_in_talento: false,
      buscando: false,
    };
  },
  validators: {
    'persona.num_doc'(value) { return Validator.value(value).required().minLength(7); },
    'persona.nombres'(value) { return Validator.value(value).regex("^[A-ZÁÉÍÓÚÑa-zñáéíóú][A-ZÁÉÍÓÚÑa-zñáéíóú]+(\\s[A-ZÁÉÍÓÚÑa-zñáéíóú][A-ZÁÉÍÓÚÑa-zñáéíóú]+)*$").required().minLength(2); },
    'persona.pri_apellido'(value) { return Validator.value(value).regex("^[A-ZÁÉÍÓÚÑa-zñáéíóú][A-ZÁÉÍÓÚÑa-zñáéíóú]+(\\s[A-ZÁÉÍÓÚÑa-zñáéíóú][A-ZÁÉÍÓÚÑa-zñáéíóú]+)*$").required().minLength(2); },
    'persona.email'(value) { return Validator.value(value).required().email(); },
    'persona.sexo'(value) { return Validator.value(value).required(); },
    'persona.tipo'(value) { return Validator.value(value).required(); },
    'persona.diestro'(value) { return Validator.value(value).required(); },
    'persona.profesiones'(value) { return Validator.value(value).required(); },
    'persona.seg_apellido'(value) { return Validator.value(value).minLength(0).regex("^[A-ZÁÉÍÓÚÑa-zñáéíóú][A-ZÁÉÍÓÚÑa-zñáéíóú]+(\\s[A-ZÁÉÍÓÚÑa-zñáéíóú][A-ZÁÉÍÓÚÑa-zñáéíóú]+)*$"); },
    'persona.domicilio'(value) { return Validator.value(value).minLength(0); },
    'usuario.login'(value) { return Validator.value(value).required().minLength(5); },
    'usuario.clave'(value) { return Validator.value(value).required().minLength(5); },
    'cargos'(value) { return Validator.value(value).required(); },
  },
  watch:{
        persona: { deep:true,handler(){ window.sessionStorage.setItem( 'xpersona',JSON.stringify(this.persona) ); } },
        usuario: { deep:true,handler(){ window.sessionStorage.setItem( 'xusuario',JSON.stringify(this.usuario) ); } },
        'persona.idprofesion': function(){ this.setProfesiones() },

        'persona.nombres': function(){ this.crearClave() },
        'persona.pri_apellido': function(){ this.crearClave() },
        'persona.seg_apellido': function(){ this.crearClave() },
        'persona.fecha_nac': function(){ this.crearClave() },
        'persona.num_doc': function(){
            let unidad = this.$store.state.CARGO.sede.unidad_negocio;
            if(unidad != 14){
                this.existe_in_talento = false;
            }
            this.clearDataRRHH(); 
            this.crearLogin();
            this.crearClave();
        },
        // 'persona.iddepartamento': function(){ this.crearClave() },
    },
    methods:{ 
        crearLogin(){
            var car = this.persona.num_doc;
            var ci = "";
            var dep = this.persona.iddepartamento;
            if( car && car.length > 0 ) ci = this.persona.num_doc;
            if( dep ) ci = ci+this.persona.iddepartamento.extension;
            this.usuario.login = ci;
            this.persona.usuario_moodle = ci;
        }, 
        crearClave(fnac = null){ 
            let f = this.persona.fecha_nac;
            if(fnac) f = fnac;  
            const a = this.persona.nombres;
            const ap = this.persona.pri_apellido;
            const am = this.persona.seg_apellido;
            const logi = this.$generarCodigoPersona(f, a, ap, am);
            this.usuario.clave = logi;
            this.persona.clave_moodle = logi; 
        },
    validarCampos() {
      this.parseBoolean();
      this.$validate().then((success) => {
        var obj = window.Object.assign({}, this.persona);
        var obj2 = window.Object.assign({}, this.usuario);
        if (success) {
          if (obj.iddepartamento) obj.iddepartamento = obj.iddepartamento.id;
          if (obj.idpais) obj.idpais = obj.idpais.id;
          if (obj.iduniversidad) obj.idinstitucion = obj.iduniversidad.id;
          if (obj.idciudad) obj.idciudad = obj.idciudad.id;
          if (obj.idprofesion) this.setProfesiones();
          delete obj.iduniversidad;
          delete obj.idprofesion;
          delete obj.fecha;
          this.$store.state.obj = obj;
          this.$store.dispatch('quitarNulos');
          this.$store.state.obj = obj2;
          this.$store.dispatch('quitarNulos');
          this.activo = false;
          this.$refs.alertar.cargando();
          this.enviarDatos(obj, obj2);
        } else {
          this.$refs.alertar.invalido();
        }
      });
    },
    setProfesiones() {
      if (this.persona.idprofesion == null) return;
      this.persona.profesiones = [];
      var vue = this;
      vue.persona.idprofesion.forEach(function(e) { vue.persona.profesiones.push(e.id); });
    },
    parseBoolean() {
      this.persona.diestro = this.persona.diestro == 'true';
    },
    async enviarDatos(obj, obj2) {
      var vue = this;
      var d = [];
      this.cargos.forEach(function(e) { d.push({ idcargo: e.idcargo, idsede: e.idsede }); });
      var sql = gql`mutation{ createUsuarioPersonaCargo(input:{ persona:${window.stringifyObject(obj)},usuario:${window.stringifyObject(obj2)},cargos:${window.stringifyObject(d)} }){ok,errors{message}} }`;
      this.$apollo.mutate({ mutation: sql }).then((data) => {
        if (data.data.createUsuarioPersonaCargo.ok) {
          vue.$refs.alertar.success();
          window.sessionStorage.setItem('xpersona', null);
          window.sessionStorage.setItem('xusuario', null);
        } else {
          var errores = JSON.parse(JSON.stringify(data.data.createUsuarioPersonaCargo.errors));
          var vals = '';
          errores.forEach(function(k) { vals += Object.values(k) + '-\n\t'; });
          vue.msg = '';
          vue.activo = true;
          vue.$refs.alertar.invalido(vals);
        }
      }).catch((error) => {
        vue.$refs.alertar.invalido();
        this.activo = true;
        window.console.error(error);
        this.$notify(parseJsonError(error));
      });
    },
    clearDataRRHH() {
      this.persona.nombres = '';
      this.persona.pri_apellido = '';
      this.persona.seg_apellido = '';
      this.persona.email = '';
      this.persona.sexo = '';
      this.persona.domicilio = '';
      this.persona.telefono1 = '';
      this.persona.fecha_nac = '';
      this.persona.iddepartamento = null;
      this.persona.idprofesion = null;
    },
    async buscarTalento() {
      if (!this.persona.num_doc) return;
      const url = process.env.VUE_APP_RRHH + '/empleados/validar?nro_ci=' + this.persona.num_doc;
      const tokenStatic = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzaXN0ZW1hIjoic2lzdGVtYV9wb3J0YWwiLCJpYXQiOjE3Mjg1MDgxNTAsImV4cCI6MzE3MjcyOTUwNTUwfQ.mo9Y8O-B4-AIoMt2rHKqVUZrDy_Z5oBZAn1VrrMLFzg';
      const h = { method: 'GET', headers: { 'Authorization': `Bearer ${tokenStatic}`, 'Content-Type': 'application/json' } };
      this.existe_in_talento = false;
      this.buscando = true;
      const res = await fetch(url, h).then(r => r.json()).then(r => r).catch(e => e);
      let cvigente = res.vigente ? 'success' : 'red';
      let mensaje = res.message ? res.message : res.mensaje;
      this.existe_in_talento = res.vigente ? true : false;
      if(this.$store.state.CARGO.sede.unidad_negocio == 15){
        this.existe_in_talento = true
      }
      this.$root.$notify.show(mensaje, { color: cvigente, x: 'right' });
      this.clearDataRRHH();
      const vue = this;
      if (res.datos) {
        window.console.log('talentoooo', res.datos);
        this.persona.nombres = res.datos.primerNombre;
        if (res.datos.otrosNombres) this.persona.nombres += ' ' + res.datos.otrosNombres;
        this.persona.pri_apellido = res.datos.apellidoPaterno;
        this.persona.seg_apellido = res.datos.apellidoMaterno;
        this.persona.email = res.datos.correoElectronico;
        this.persona.sexo = res.datos.sexo;
        this.persona.domicilio = res.datos.direccion;
        this.persona.telefono1 = res.datos.celular;
        setTimeout(() => {
          vue.$refs.refDepartamentos.findCi(res.datos.expedidoCi);
          vue.$refs.refProfesiones.findPf(res.datos.profesionOcupacion);
          vue.$refs.refFecha.findFn(res.datos.fechaNacimiento);
        }, 300);
      }
      this.buscando = false;
    },
  },
  beforeDestroy() {},
  mounted() {
    let unidad = this.$store.state.CARGO.sede.unidad_negocio;
    if (unidad == 14) this.existe_in_talento = true;
  },
};
</script>

<style scoped>
.ua-page { min-height: 100vh; background: #f5f7fa; }

.ua-hero {
  background: linear-gradient(120deg, #1a237e 0%, #283593 50%, #1565c0 100%);
  padding: 20px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.ua-hero__title { font-size: 1.15rem; font-weight: 700; color: #fff; letter-spacing: 0.02em; }
.ua-hero__sub   { font-size: 0.78rem; color: rgba(255,255,255,0.65); margin-top: 2px; }

.ua-body { padding: 20px 24px; }

.ua-section-card { border-radius: 10px !important; border-color: #e3e8f0 !important; }

.ua-section-title {
  font-size: 0.82rem !important;
  font-weight: 700 !important;
  color: #283593;
  letter-spacing: 0.02em;
  border-bottom: 1px solid #e8edf7;
}

.ua-field-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #7986cb;
}

.ua-field-error {
  font-size: 0.75rem;
  color: #c62828;
  margin-top: 2px;
}
</style>
